'use client';

import { TextField, InputAdornment, IconButton } from '@mui/material';
import { useEffect, useRef, useState, KeyboardEventHandler } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';

import { useDebounce } from '@web/hooks';

type SearchBarProps = {
  label?: string;
  placeholder?: string;

  value?: string; // optional → supports uncontrolled mode
  onChange?: (value: string) => void;
  onDebouncedChange?: (value: string) => void;

  debounceMs?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  fullWidth?: boolean;
  autoComplete?: 'on' | 'off';
};

export function SearchBar({
  label,
  placeholder,
  value,
  onChange,
  onDebouncedChange,
  debounceMs = 300,
  disabled,
  autoFocus = false,
  fullWidth = true,
  autoComplete = 'on',
}: SearchBarProps) {
  const isControlled = value !== undefined && typeof onChange === 'function';

  const [internalValue, setInternalValue] = useState(value ?? '');

  useEffect(() => {
    if (value !== undefined) {
      // eslint-disable-next-line
      setInternalValue(value);
    }
  }, [value]);

  const actualValue = isControlled ? value! : internalValue;

  const debounced = useDebounce(actualValue, debounceMs);
  const lastEmittedRef = useRef('');
  const onDebouncedChangeRef = useRef(onDebouncedChange);

  useEffect(() => {
    onDebouncedChangeRef.current = onDebouncedChange;
  }, [onDebouncedChange]);

  useEffect(() => {
    const debouncedChange = onDebouncedChangeRef.current;
    if (!debouncedChange) return;

    if (debounced !== lastEmittedRef.current) {
      debouncedChange(debounced);
      lastEmittedRef.current = debounced;
    }
  }, [debounced]);

  const handleChange = (v: string) => {
    if (!isControlled) setInternalValue(v);
    onChange?.(v);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const debouncedChange = onDebouncedChangeRef.current;
    if (e.key === 'Enter' && debouncedChange) {
      debouncedChange(actualValue);
      lastEmittedRef.current = actualValue;
    }
  };

  const clear = () => {
    const next = '';
    handleChange(next);

    const debouncedChange = onDebouncedChangeRef.current;
    if (debouncedChange) {
      debouncedChange(next);
    }

    lastEmittedRef.current = next;
  };

  return (
    <TextField
      fullWidth={fullWidth}
      label={label}
      placeholder={placeholder}
      value={actualValue}
      onChange={(e) => handleChange(e.target.value)}
      onKeyDown={handleKeyDown}
      autoFocus={autoFocus}
      disabled={disabled}
      autoComplete={autoComplete}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <FiSearch size={18} color="var(--nn-color-text)" />
            </InputAdornment>
          ),
          endAdornment: actualValue ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                onClick={clear}
              >
                <FiX size={16} color="var(--nn-color-text)" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
      sx={{
        '& .MuiInputBase-root': {
          padding: '0.5rem 1rem',
          height: '3rem',
        },
        '& .MuiOutlinedInput-input': {
          padding: '0.25rem 0.7rem !important',
        },
      }}
    />
  );
}
